<template>
  <div class="certification-region-card">
    <div class="font-weight-medium mb-3" style="color: #302F44">
      <translate translate-context="vatDataForm.region">Region of origin</translate>
    </div>

    <!-- EU radio -->
    <div class="d-flex align-items-start mb-2">
      <div class="custom-control custom-radio mr-2 mt-0">
        <input
          type="radio"
          class="custom-control-input"
          :id="uid + '-region-eu'"
          value="eu"
          v-model="innerForm.region"
          :disabled="disabled"
          @change="onRegionChange"
        />
        <label class="custom-control-label" :for="uid + '-region-eu'">
          <translate translate-context="vatDataForm.region">I am established in the European Union.</translate>
        </label>
      </div>
    </div>

    <!-- EU: VAT section -->
    <div v-if="isEu" class="ml-4 pl-2 mt-3 mb-3">
      <div class="font-weight-medium mb-2" style="font-size: 14px;">
        <translate translate-context="vatDataForm.vat">Enter your VAT number below:</translate>
      </div>
      <div class="d-flex align-items-center certification-vat-input">
        <div class="position-relative flex-grow-1 mr-2">
          <input
            ref="vatInput"
            type="text"
            maxlength="20"
            placeholder="IT02118311006"
            v-model="innerForm.vat_number"
            :disabled="disabled"
            :class="['form-control', vatInputClass]"
            @input="onVatNumberChange"
          />
        </div>
        <CIcon
          v-if="vatValidation === true"
          name="cipCheck"
          class="text-success mr-2"
          size="custom-size"
          width="16"
          height="16"
        />
        <CButton
          color="primary"
          variant="outline"
          size="sm"
          :disabled="disabled || !innerForm.vat_number || isValidatingVat || vatValidation === true || !!vatSyntaxError"
          @click="handleValidateVat"
        >
          <CSpinner v-if="isValidatingVat" size="sm" class="mr-1" />
          <translate translate-context="vatDataForm.vat">Verify</translate>
        </CButton>
      </div>
      <div class="mt-1" style="font-size: 12px; color: #A9A8C2;">
        <translate translate-context="vatDataForm.vat">E.g. IT12345678901</translate>
      </div>

      <!-- VAT syntax error -->
      <div
        v-if="vatSyntaxError"
        class="text-danger mt-1"
        style="font-size: 12px"
      >
        {{ vatSyntaxError }}
      </div>

      <!-- VAT country code error -->
      <div
        v-if="vatCountryCodeError"
        class="text-danger mt-1"
        style="font-size: 12px"
      >
        {{ vatCountryCodeError }}
      </div>

      <!-- VAT validation error -->
      <div
        v-if="vatValidation === false"
        class="text-warning mt-2"
        style="font-size: 12px"
      >
        <translate translate-context="vatDataForm.vat">
          We were unable to recognize your VAT number. Please make sure you entered it correctly. Otherwise, we ask you to upload a document certifying your company's existence (it will be approved after 48 hours).
        </translate>
      </div>

      <!-- VIES error -->
      <div
        v-if="vatValidationError"
        class="text-warning mt-2"
        style="font-size: 12px"
      >
        {{ vatValidationError }}
      </div>

      <!-- EU: Document upload (shown after VAT validation failure) -->
      <div v-if="showDocumentUpload && isEu" class="mt-3">
        <div class="font-weight-medium mb-2" style="font-size: 14px;">
          <translate translate-context="vatDataForm.document">Company certification</translate>
        </div>
        <VatDataFormUpload
          :document="innerForm.document"
          :disabled="disabled"
          @file="onFileSelected"
          @remove="removeFile"
        />
      </div>
    </div>

    <!-- Extra EU radio -->
    <div class="d-flex align-items-start" :class="{ 'mt-3': !isEu }">
      <div class="custom-control custom-radio mr-2 mt-0">
        <input
          type="radio"
          class="custom-control-input"
          :id="uid + '-region-extra-eu'"
          value="extra_eu"
          v-model="innerForm.region"
          :disabled="disabled"
          @change="onRegionChange"
        />
        <label class="custom-control-label" :for="uid + '-region-extra-eu'">
          <translate translate-context="vatDataForm.region">I am established in a non-EU country.</translate>
        </label>
      </div>
    </div>

    <!-- Extra EU: Document upload -->
    <div v-if="!isEu" class="ml-4 pl-2 mt-3">
      <div class="font-weight-medium mb-2" style="font-size: 14px;">
        <translate translate-context="vatDataForm.document">
          Upload a document containing your tax data, in accordance with the applicable regulations:
        </translate>
      </div>
      <VatDataFormUpload
        :document="innerForm.document"
        :disabled="disabled"
        @file="onFileSelected"
        @remove="removeFile"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import VatDataFormUpload from '@/components/VatDataFormUpload.vue'

let uidCounter = 0

export default {
  name: 'VatDataForm',
  components: { VatDataFormUpload },
  props: {
    value: {
      type: Object,
      default: () => ({ region: 'eu', vat_number: '', document: null })
    },
    disabled: {
      type: Boolean,
      default: false
    },
    contractManagerId: {
      type: [Number, String],
      required: true
    },
  },
  data() {
    return {
      uid: `vat-form-${uidCounter++}`,
      isValidatingVat: false,
      vatValidation: null,
      vatValidationError: null,
      vatCountryCodeError: null,
      innerForm: {
        region: this.value.region || 'eu',
        vat_number: this.value.vat_number || '',
        document: this.value.document || null
      }
    }
  },
  computed: {
    isEu() {
      return this.innerForm.region === 'eu'
    },
    showDocumentUpload() {
      if (!this.isEu) return true
      return this.vatValidation === false || this.vatValidationError !== null
    },
    vatSyntaxError() {
      const v = this.innerForm.vat_number
      if (!v) return null
      if (v.length < 3) return null
      const countryMatch = v.match(/^([A-Z]{2,3})/)
      if (!countryMatch) return null
      const countryCode = countryMatch[1]
      const digits = v.substring(countryCode.length)
      if (!/^\d+$/.test(digits)) {
        return this.$pgettext('vatDataForm.vat', 'After the country code, only numbers are allowed.')
      }
      if (countryCode === 'IT') {
        if (digits.length !== 11) {
          return this.$pgettext('vatDataForm.vat', 'Italian VAT number must have exactly 11 digits.')
        }
        if (!this.isValidItalianVat(digits)) {
          return this.$pgettext('vatDataForm.vat', 'Invalid Italian VAT number (checksum error).')
        }
      }
      return null
    },
    vatValidationState() {
      if (this.vatValidation === true) return true
      if (this.vatValidation === false || this.vatSyntaxError || this.vatCountryCodeError) return false
      return null
    },
    vatInputClass() {
      if (this.vatValidationState === true) return 'is-valid'
      if (this.vatValidationState === false) return 'is-invalid'
      return ''
    },
    isValid() {
      if (!this.innerForm.region) return false
      if (this.isEu) {
        if (this.vatValidation === true && this.innerForm.vat_number) return true
        return !!this.innerForm.document
      }
      return !!this.innerForm.document
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal) {
          this.innerForm.region = newVal.region || 'eu'
          this.innerForm.vat_number = newVal.vat_number || ''
          this.innerForm.document = newVal.document || null
        }
      },
      deep: true
    },
    isValid: {
      handler(val) {
        this.$emit('validity-change', val)
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('payout', ['validateVat']),
    emitInput() {
      this.$emit('input', { ...this.innerForm })
    },
    isValidItalianVat(digits) {
      let sum = 0
      for (let i = 0; i < 11; i++) {
        const d = parseInt(digits[i], 10)
        if (i % 2 === 0) {
          sum += d
        } else {
          const doubled = d * 2
          sum += doubled > 9 ? doubled - 9 : doubled
        }
      }
      return sum % 10 === 0
    },
    onRegionChange() {
      this.vatValidation = null
      this.vatValidationError = null
      this.vatCountryCodeError = null
      this.innerForm.document = null
      this.innerForm.vat_number = ''
      this.emitInput()
    },
    onVatNumberChange(event) {
      const sanitized = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
      event.target.value = sanitized
      this.innerForm.vat_number = sanitized
      if (this.vatValidation !== null || this.vatCountryCodeError) {
        this.vatValidation = null
        this.vatValidationError = null
        this.vatCountryCodeError = null
      }
      this.emitInput()
    },
    async handleValidateVat() {
      if (!this.innerForm.vat_number) return

      const countryMatch = this.innerForm.vat_number.match(/^([A-Z]{2,3})/)
      if (!countryMatch) {
        this.vatCountryCodeError = this.$pgettext(
          'vatDataForm.vat',
          'Remember to put the country code before your VAT number'
        )
        return
      }

      this.vatValidation = null
      this.vatValidationError = null
      this.isValidatingVat = true

      try {
        const result = await this.validateVat({ vatNumber: this.innerForm.vat_number, cmId: this.contractManagerId })
        this.vatValidation = result.is_valid
        if (result.is_valid) {
          this.innerForm.document = null
          this.emitInput()
        }
      } catch (error) {
        const status = error?.response?.status || error?.status
        if (status === 429) {
          this.vatValidationError = this.$pgettext(
            'vatDataForm.vat',
            'Too many verification attempts. Please try again in a few minutes.'
          )
        } else if (status === 503) {
          this.vatValidationError = this.$pgettext(
            'vatDataForm.vat',
            'Verification service is currently unavailable. Please try again later or upload a document.'
          )
        } else {
          this.vatValidationError = this.$pgettext(
            'vatDataForm.vat',
            'Error during verification. Please try again or upload a document.'
          )
        }
      } finally {
        this.isValidatingVat = false
      }
    },
    onFileSelected(file) {
      this.innerForm.document = file
      this.emitInput()
    },
    removeFile() {
      this.innerForm.document = null
      this.emitInput()
    },
    getFormData() {
      const formData = new FormData()
      formData.append('region', this.innerForm.region)
      if (this.innerForm.vat_number) {
        formData.append('vat_number', this.innerForm.vat_number.replace(/\s/g, ''))
      }
      if (this.innerForm.document && this.innerForm.document instanceof File) {
        formData.append('document', this.innerForm.document)
      }
      return formData
    }
  }
}
</script>

<style scoped>
.certification-region-card {
  background-color: #FAFAFA;
  border: 1px solid #EDEBFF;
  border-radius: 12px;
  padding: 16px;
}

.certification-vat-input .form-control {
  margin-bottom: 0;
}
</style>
